import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';

import toast from 'src/common/toast';
import { Button } from 'src/common/components/buttons/Button';
import { PasswordInput } from 'src/common/components/inputs/PasswordInput/PasswordInput';
import { TextInput } from 'src/common/components/inputs/TextInput/TextInput';
import { ExternalTextLink, TextLink } from 'src/common/components/TextLink';
import { useAuth } from 'src/auth/user-context';
import { AuthLayout } from '../../components/AuthLayout';

const MARKETING_WEBSITE_URL =
  'https://www.doka.com/at/solutions/services/site-docu-and-analytics';

export const LoginView = () => {
  const { login } = useAuth();
  const { t } = useTranslation('auth');
  const { search } = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch (err) {
      toast.error(t('loggedError'));
    }
  };

  return (
    <AuthLayout aside={<MarketingCopy />}>
      <form method="post" onSubmit={handleSubmit} className="px-8 max-md:px-4">
        <TextInput
          label={t('email')}
          placeholder={t('email')}
          value={email}
          name="email"
          className="mt-3 w-full max-md:mt-1"
          onChange={(e) => setEmail(e.target.value)}
          id="email"
          required
        />

        <PasswordInput
          label={t('password')}
          placeholder={t('password')}
          value={password}
          name="password"
          className="mt-3 w-full max-md:mt-2"
          onChange={(e) => setPassword(e.target.value)}
          id="password"
          required
        />

        <div className="mt-8 flex w-full flex-col gap-2 text-base max-md:mt-4">
          <Button type="submit">
            {`${t('login')}${
              import.meta.env.VITE_ENVIRONMENT === 'staging'
                ? ` ${t('test')}`
                : ''
            }`}
          </Button>
        </div>
      </form>
      <div className="mt-2 py-2 text-center max-md:mt-1 max-md:py-0">
        <TextLink to="/forgot-password">{`${t('forgotPassword')}`}</TextLink>
      </div>
      <hr className="my-4 text-gray-300 max-md:my-1" />
      <div className="px-8 pt-2 max-md:px-4 max-md:pt-0">
        <h1 className="text-l mb-2 font-semibold max-md:mb-1">
          {t('newToSitelife')}
        </h1>
        <Button
          className="mt-2 w-full max-md:mt-1"
          variant="secondary"
          onClick={() => navigate(`/sign-up${search}`)}
        >
          {`${t('toSignUp')}`}
        </Button>
      </div>
    </AuthLayout>
  );
};

const MarketingCopy = () => {
  const { t } = useTranslation('auth');

  return (
    <>
      <h1 className="mb-4 mt-11 text-4xl font-semibold max-md:mb-2 max-md:mt-0 max-md:text-2xl">
        {t('marketingAside.title')}
      </h1>

      <div className="md:hidden">
        <p className="mb-4 text-shuttleGray-600 max-md:mb-2 max-md:text-sm max-md:leading-snug">
          <Trans ns="auth" i18nKey="marketingAside.mobileNotice" />
        </p>
        <p className="mb-4 text-shuttleGray-600 max-md:mb-0 max-md:text-sm max-md:leading-snug">
          <Trans ns="auth" i18nKey="marketingAside.visitDesktop">
            Visit the{' '}
            <ExternalTextLink href={MARKETING_WEBSITE_URL}>
              website
            </ExternalTextLink>{' '}
            with a Desktop or larger device to see full functionality.
          </Trans>
        </p>
      </div>

      <div className="hidden md:block">
        <p className="mb-4 text-shuttleGray-600">
          {t('marketingAside.description')}
        </p>
        <ul className="mb-6">
          <li className="mb-2 flex items-center gap-2">
            <FontAwesomeIcon
              fixedWidth
              icon={faCheckCircle}
              className="text-ugLime-600"
            />
            <span>
              <Trans ns="auth" i18nKey="marketingAside.list.planning">
                Einfache <strong>Planung</strong>
              </Trans>
            </span>
          </li>
          <li className="mb-2 flex items-center gap-2">
            <FontAwesomeIcon
              fixedWidth
              icon={faCheckCircle}
              className="text-ugLime-600"
            />
            <span>
              <Trans ns="auth" i18nKey="marketingAside.list.documentation">
                Modellbasierte <strong>Dokumentation</strong>
              </Trans>
            </span>
          </li>
          <li className="flex items-center gap-2">
            <FontAwesomeIcon
              fixedWidth
              icon={faCheckCircle}
              className="text-ugLime-600"
            />
            <span>
              <Trans ns="auth" i18nKey="marketingAside.list.analysis">
                Kontextbezogene <strong>Analyse</strong>
              </Trans>
            </span>
          </li>
        </ul>

        <p className="mb-4 text-shuttleGray-600">
          <Trans ns="auth" i18nKey="marketingAside.visitWebsite">
            Besuchen Sie unsere{' '}
            <ExternalTextLink href={MARKETING_WEBSITE_URL}>
              Website
            </ExternalTextLink>{' '}
            und erfahren Sie viele weitere Informationen rund um Site Docu and
            Analytics.
          </Trans>
        </p>
      </div>
    </>
  );
};
